import { api } from './api';

export interface ContractType {
  id: string;
  code: string;
  label: string;
  isActive: boolean;
  // Applicabilité par type de prospect. Au moins un des deux flags doit être
  // true (CHECK DB `contract_types_applicability_chk` + validation service).
  applicableToCompany: boolean;
  applicableToIndividual: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InsuranceCompany {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChecklistTemplate {
  id: string;
  contractTypeId: string;
  documentName: string;
  isRequired: boolean;
  sortOrder: number;
  contractType?: { id: string; code: string; label: string };
}

export interface OrganizationProfile {
  name?: string;
  siret?: string;
  orias?: string;
  address?: {
    street?: string;
    city?: string;
    zip?: string;
    country?: string;
  };
  brandColor?: string;
  logoAlign?: 'left' | 'center';
  hasLogo: boolean;
  hasFavicon: boolean;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  category:
    | 'quote_send'
    | 'quote_followup'
    | 'document_request'
    | 'meeting_confirm'
    | 'signature_thanks'
    | 'other';
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  creator?: { id: string; firstName: string; lastName: string };
}

export interface EmailPreview {
  subject: string;
  body: string;
  variablesUsed: string[];
  supportedVariables: string[];
}

export type Settings = Record<string, unknown>;

// ---------------- Users (admin CRUD) ----------------

export type UserRole = 'admin' | 'director' | 'manager' | 'collaborator' | 'executive';

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  teamId: string | null;
  isActive: boolean;
  mfaEnabled: boolean;
  forcePasswordChange: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  team: { id: string; name: string } | null;
}

export interface CreatedUserResponse {
  user: AdminUser;
  temporaryPassword: string;
}

export interface ResetPasswordResponse {
  userId: string;
  temporaryPassword: string;
}

export interface UserActivityItem {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  metadata: unknown;
  createdAt: string;
}

export interface UserActivityResponse {
  items: UserActivityItem[];
  nextCursor: string | null;
}

export const configApi = {
  // ---- contract types ----
  listContractTypes: (token: string) =>
    api.get<ContractType[]>('/api/v1/config/contract-types', { accessToken: token }),
  createContractType: (
    token: string,
    body: {
      code: string;
      label: string;
      applicableToCompany?: boolean;
      applicableToIndividual?: boolean;
    },
  ) => api.post<ContractType>('/api/v1/config/contract-types', body, { accessToken: token }),
  updateContractType: (
    token: string,
    id: string,
    body: {
      label?: string;
      isActive?: boolean;
      applicableToCompany?: boolean;
      applicableToIndividual?: boolean;
    },
  ) => api.patch<ContractType>(`/api/v1/config/contract-types/${id}`, body, { accessToken: token }),

  // ---- insurance companies ----
  listInsuranceCompanies: (token: string, activeOnly?: boolean) =>
    api.get<InsuranceCompany[]>(
      `/api/v1/config/insurance-companies${activeOnly ? '?active_only=true' : ''}`,
      { accessToken: token },
    ),
  createInsuranceCompany: (token: string, body: { name: string }) =>
    api.post<InsuranceCompany>('/api/v1/config/insurance-companies', body, { accessToken: token }),
  updateInsuranceCompany: (
    token: string,
    id: string,
    body: { name?: string; isActive?: boolean },
  ) =>
    api.patch<InsuranceCompany>(`/api/v1/config/insurance-companies/${id}`, body, {
      accessToken: token,
    }),

  // ---- checklist templates ----
  listChecklistTemplates: (token: string, contractTypeId?: string) =>
    api.get<ChecklistTemplate[]>(
      `/api/v1/config/checklist-templates${contractTypeId ? `?contract_type_id=${contractTypeId}` : ''}`,
      { accessToken: token },
    ),
  createChecklistTemplate: (
    token: string,
    body: {
      contractTypeId: string;
      documentName: string;
      isRequired?: boolean;
      sortOrder?: number;
    },
  ) =>
    api.post<ChecklistTemplate>('/api/v1/config/checklist-templates', body, { accessToken: token }),
  updateChecklistTemplate: (
    token: string,
    id: string,
    body: { documentName?: string; isRequired?: boolean; sortOrder?: number },
  ) =>
    api.patch<ChecklistTemplate>(`/api/v1/config/checklist-templates/${id}`, body, {
      accessToken: token,
    }),
  deleteChecklistTemplate: (token: string, id: string) =>
    api.delete<void>(`/api/v1/config/checklist-templates/${id}`, { accessToken: token }),

  // ---- settings ----
  getSettings: (token: string) =>
    api.get<Settings>('/api/v1/config/settings', { accessToken: token }),
  getPublicSettings: (token: string) =>
    api.get<Settings>('/api/v1/config/public-settings', { accessToken: token }),
  updateSettings: (token: string, body: Record<string, number | boolean>) =>
    api.patch<Settings>('/api/v1/config/settings', body, { accessToken: token }),

  // ---- email templates ----
  listEmailTemplates: (token: string) =>
    api.get<EmailTemplate[]>('/api/v1/email-templates', { accessToken: token }),
  createEmailTemplate: (
    token: string,
    body: Pick<EmailTemplate, 'name' | 'subject' | 'body' | 'category'> & { isActive?: boolean },
  ) => api.post<EmailTemplate>('/api/v1/email-templates', body, { accessToken: token }),
  updateEmailTemplate: (
    token: string,
    id: string,
    body: Partial<Pick<EmailTemplate, 'name' | 'subject' | 'body' | 'category' | 'isActive'>>,
  ) => api.patch<EmailTemplate>(`/api/v1/email-templates/${id}`, body, { accessToken: token }),
  deleteEmailTemplate: (token: string, id: string) =>
    api.delete<void>(`/api/v1/email-templates/${id}`, { accessToken: token }),
  previewEmailTemplate: (token: string, id: string, variables?: Record<string, string>) =>
    api.post<EmailPreview>(
      `/api/v1/email-templates/${id}/preview`,
      { variables },
      { accessToken: token },
    ),

  // ---- users (admin) ----
  listUsers: (token: string) => api.get<AdminUser[]>('/api/v1/users', { accessToken: token }),
  getUser: (token: string, id: string) =>
    api.get<AdminUser>(`/api/v1/users/${id}`, { accessToken: token }),
  createUser: (
    token: string,
    body: {
      email: string;
      firstName: string;
      lastName: string;
      role: UserRole;
      teamId?: string | null;
    },
  ) => api.post<CreatedUserResponse>('/api/v1/users', body, { accessToken: token }),
  updateUser: (
    token: string,
    id: string,
    body: Partial<{
      firstName: string;
      lastName: string;
      role: UserRole;
      teamId: string | null;
      isActive: boolean;
    }>,
  ) => api.patch<AdminUser>(`/api/v1/users/${id}`, body, { accessToken: token }),
  resetUserPassword: (token: string, id: string) =>
    api.patch<ResetPasswordResponse>(`/api/v1/users/${id}/password`, {}, { accessToken: token }),
  getUserActivity: (token: string, id: string, cursor?: string) =>
    api.get<UserActivityResponse>(
      `/api/v1/users/${id}/activity${cursor ? `?cursor=${cursor}` : ''}`,
      { accessToken: token },
    ),

  // ---- organization / entreprise ----
  getOrganization: (token: string) =>
    api.get<OrganizationProfile>('/api/v1/config/organization', { accessToken: token }),
  updateOrganization: (
    token: string,
    body: Partial<Omit<OrganizationProfile, 'hasLogo' | 'hasFavicon'>>,
  ) => api.patch<OrganizationProfile>('/api/v1/config/organization', body, { accessToken: token }),
  uploadOrganizationLogo: async (token: string, file: File): Promise<{ hasLogo: boolean }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch('/api/v1/config/organization/logo', {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    const text = await res.text();
    const data = text ? JSON.parse(text) : null;
    if (!res.ok) {
      const err = (data as { error?: { code: string; message: string } } | null)?.error;
      throw new Error(err?.message ?? 'Upload logo failed');
    }
    return data as { hasLogo: boolean };
  },
  uploadOrganizationFavicon: async (
    token: string,
    file: File,
  ): Promise<{ hasFavicon: boolean }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch('/api/v1/config/organization/favicon', {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    const text = await res.text();
    const data = text ? JSON.parse(text) : null;
    if (!res.ok) {
      const err = (data as { error?: { code: string; message: string } } | null)?.error;
      throw new Error(err?.message ?? 'Upload favicon failed');
    }
    return data as { hasFavicon: boolean };
  },
  deleteOrganizationLogo: (token: string) =>
    api.delete<void>('/api/v1/config/organization/logo', { accessToken: token }),
  deleteOrganizationFavicon: (token: string) =>
    api.delete<void>('/api/v1/config/organization/favicon', { accessToken: token }),
};
